import {first} from 'lodash';
import React, {FC, ForwardedRef, PropsWithChildren, useMemo} from 'react';
import styled, {css} from 'styled-components';

import {RendererOf} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {MessageAnnotationModel} from '../../../../../../../libs/shared/core/src/models/api/annotationModel';
import {MessageTypesEnum} from '../../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {
  isLoadedMessage,
  messageHasDecisionTreesOrQualBotsResponses,
  MessageModel,
  MessageVisualStatusesEnum,
} from '../../../../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {CellsContext} from '../../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {ellipsis} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {boxShadow} from '../../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {isDarkTheme} from '../../../../../../../libs/shared/ui/src/theme/themeTypeHelpers';
import {UserTheme} from '../../../../../../../libs/shared/ui/src/theme/themeTypes';
import {buildContactDisplayName} from '../../../../../../helpers/models/contactHelpers';
import {VisualRecipientMap} from '../../../../../../helpers/models/recipientHelpers';
import {ActivitySourceVisualTypesEnum} from '../../../../../../models/api/activitySourceModel';
import {computeIsAutoResponseMessageActivitySource} from '../../../../../../store/data/activities/autoResponseMessageActivityHelpers';
import {AnnotationsHighlightViewerInheritableProps} from '../../../../../common/annotation/annotationsHighlightViewer';
import {messageViewerMessageContainerClassName} from '../../../../../onboarding/sandbox/educationTips/messageViewerEducationTip';
import {MessageViewerChatBody} from '../chat/messageViewerChatBody';
import {MessageViewerAttachmentsContainer} from '../messageViewerAttachmentsContainer';
import {MessageViewerAvatar} from '../messageViewerAvatar';
import {MessageViewerChatSeen} from '../messageViewerChatSeen';
import {MessageViewerDate} from '../messageViewerDate';
import {MessageViewerDetails} from '../messageViewerDetails';
import {MessageViewerMoreBaseProps} from '../messageViewerMore';
import {MessageViewerMoreContainer} from '../messageViewerMoreContainer';
import {MessageViewerSeen} from '../messageViewerSeen';
import {MessageViewerVisitorUrlPopover} from '../messageViewerVisitorUrlPopover';
import {MessageViewerSender} from '../sender/messageViewerSender';
import {MessageViewerUnread, MessageViewerUnreadProps} from '../sender/messageViewerUnread';

/*
 * Props.
 */

interface MessageViewerSynchronousProps
  extends Omit<AnnotationsHighlightViewerInheritableProps<MessageAnnotationModel>, 'message'>,
    Omit<MessageViewerMoreBaseProps, 'message'> {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
  renderBanner?: RendererOf<RenderBannerOptions>;
  message: MessageModel;
  author?: TeammateModel;
  isChatUserOnline: boolean;
  visualRecipients: VisualRecipientMap;
  isImageMessage: boolean;
  isChatFollowUp: boolean;
  isInDragRegion?: boolean;
  cellsContext: CellsContext;
  activitySourceVisualType: ActivitySourceVisualTypesEnum | undefined;
  widgetCompanyName?: string;
  isMessageViewerExpanded: boolean;
  scrollerVisibilityRef?: ForwardedRef<HTMLDivElement>;
  isNewUnread?: MessageViewerUnreadProps['isNewUnread'];
  isUserAuthor?: MessageViewerUnreadProps['isUserAuthor'];
  activityId: number | undefined;
  needsSeenPill: boolean;
}

interface RenderBannerOptions {
  isRounded: boolean;
}

/*
 * Constants.
 */

const timelineVariation = TimelineVariationsEnum.SYNCHRONOUS;

/*
 * Style.
 */

interface WrapperStyleProps {
  $isInbound: boolean;
}

const StyledWrapperDiv = styled.div<WrapperStyleProps>`
  display: grid;
  ${(p) => addGridStyles(p.$isInbound)};
`;

function addGridStyles(isInbound: boolean) {
  if (isInbound) {
    return css`
      grid-template-areas: 'avatar . message timestamp .';
      grid-template-columns: minmax(30px, auto) 8px auto auto 1fr;
    `;
  }

  return css`
    grid-template-areas: '. timestamp message . avatar';
    grid-template-columns: 1fr auto auto 8px minmax(30px, auto);
  `;
}

const StyledMessageWrapper = styled.div`
  grid-area: message;
  max-width: 530px;
`;

interface MessageContainerStyleProps {
  $isFollowup: boolean;
  $areRecipientsHidden: boolean;
  $hasOneAttahmentOnly: boolean;
  $isForm: boolean;
}
const StyledMessageContainerDiv = styled.div<MessageContainerStyleProps>`
  background-color: ${(p) => p.theme.message.background};
  border-radius: 16px;

  ${(p) => addContainerShadow(p, p.theme)};
  ${(p) => addContainerGridStyles(p)};
  ${(p) => addWidthStyles(p)};
`;

function addWidthStyles(props: MessageContainerStyleProps) {
  return css`
    min-width: 192px;
    max-width: 530px;
    min-height: ${props.$isFollowup ? '48px' : '70px'};
    ${props.$hasOneAttahmentOnly ? 'width: 192px' : ''};
    ${props.$isForm ? 'width: 500px' : ''};
  `;
}

function addContainerShadow(_props: MessageContainerStyleProps, theme: UserTheme) {
  if (isDarkTheme(theme)) {
    return '';
  }

  return css`
    border: 1px solid ${theme.alphas.black20};
    ${(p) => boxShadow(p.theme, `0 1px 6px ${p.theme.alphas.black20}, 0 4px 30px rgba(0, 0, 0, 0)`)};
  `;
}

function addContainerGridStyles(props: MessageContainerStyleProps) {
  if (props.$areRecipientsHidden) {
    return css`
      display: grid;
      grid-auto-rows: auto;
      grid-template-areas: 'content actions' 'attachments attachments';
      grid-template-columns: 1fr auto;
      align-items: flex-start;
    `;
  }
  return css`
    display: grid;
    grid-auto-rows: auto;
    grid-template-areas:
      'recipients actions'
      'content content';
    grid-template-columns: 1fr auto;
  `;
}

const StyledMessageAvatarDiv = styled.div`
  grid-area: avatar;
  align-self: flex-start;
  margin-top: 1px;
`;

const StyledMessageViewerAvatar = styled(MessageViewerAvatar)`
  margin-top: 0;
  margin-left: 0;
  margin-right: 0;
`;

const StyledTimestampDiv = styled.div`
  grid-area: timestamp;
  padding-top: 15px;
  padding-left: 5px;
  padding-right: 5px;
`;

const StyledRecipientsDiv = styled.div<{$needsDetails: boolean}>`
  grid-area: recipients;
  padding-top: ${(p) => (p.$needsDetails ? '8px' : '11px')};
  padding-left: 9px;
  padding-right: 10px;
  padding-bottom: 7px;

  &,
  & > div {
    ${ellipsis()};
  }

  ${(p) =>
    !p.$needsDetails &&
    css`
      padding-left: 16px;
    `};
`;

const StyledActionsDiv = styled.div<{$needsDetails: boolean}>`
  grid-area: actions;
  padding-top: 10px;
  padding-right: 10px;
  display: flex;
  flex-direction: row;
  align-items: ${(p) => (p.$needsDetails ? 'flex-start' : 'center')};
  gap: 0px;
`;

const StyledSeenDiv = styled.div`
  margin-right: 3px;
  display: flex;
  flex-direction: row;
  gap: 6px;
`;

const StyledMessageContentDiv = styled.div<{
  $isFollowup: boolean;
  $noTopPadding: boolean;
  $reducedBottomPadding: boolean;
}>`
  grid-area: content;
  padding-left: 15px;
  padding-right: 15px;
  padding-top: ${(p) => (p.$noTopPadding ? 0 : '13px')};
  padding-bottom: 14px;
  overflow: hidden;
`;

const StyledMessageViewerChatBody = styled(MessageViewerChatBody)<{$isFollowup: boolean}>`
  padding-left: 0;
  padding-right: 0;
  padding-bottom: 0;
  padding-top: 0;
`;

const StyledMessageAttachmentsInContentDiv = styled.div`
  &:not(:only-child) {
    padding-top: 8px;
  }
  &:only-child {
    padding-top: 5px;
  }
`;

const StyledMessageAttachmentsStandaloneDiv = styled.div`
  grid-area: attachments;
  padding-left: 16px;
  padding-right: 16px;
  padding-bottom: 15px;
`;

/*
 * Component.
 */

/**
 * Synchronous chat message viewer.
 *
 * Used for sent chat messages and synchronous custom channels.
 *
 * @see MessageViewerChat
 */
export const MessageViewerSynchronous: FC<PropsWithChildren<MessageViewerSynchronousProps>> = (props) => {
  const {renderBanner} = props;
  const banner = renderBanner && renderBanner({isRounded: true});

  const layerExtra =
    props.message.visualStatus === MessageVisualStatusesEnum.TIMELINE ? props.message.layerExtra : undefined;
  const qualBotResponses = layerExtra ? layerExtra.qualBotResponses : [];
  const isUserVerified = layerExtra ? !layerExtra.isUserUnverified : false;
  const hasDecisionTreesOrQualBotsResponses = messageHasDecisionTreesOrQualBotsResponses(props.message);
  const messageHasBody =
    ((isLoadedMessage(props.message) && props.message.body) || '').trim().length > 0 ||
    hasDecisionTreesOrQualBotsResponses;
  const {to} = props.visualRecipients;
  // If we have multiple "to" recipients, show all of them.
  const needsDetails = Boolean(to && to.length > 1);
  const hasAttachments = isLoadedMessage(props.message) && props.message.attachments.length > 0;

  function renderRecipients() {
    if (needsDetails && isLoadedMessage(props.message)) {
      return (
        <MessageViewerDetails
          searchQuery={props.cellsContext.searchQuery}
          activitySourceVisualType={props.activitySourceVisualType}
          hasMessageAnnotations={props.hasMessageAnnotations}
          isMessageViewerExpanded
          message={props.message}
          panelId={props.panelId}
          visualRecipients={props.visualRecipients}
          annotations={props.annotations}
          author={props.author}
          shouldHideSubject
          isUserAuthor={props.isUserAuthor}
          hasSmallFont
          // We show the "new" pill separately when needed
          isNewUnread={false}
        />
      );
    }

    // Otherwise, just show the sender.
    return (
      <MessageViewerSender
        searchQuery={props.cellsContext.searchQuery}
        activitySourceVisualType={props.activitySourceVisualType}
        isMessageViewerExpanded
        message={props.message}
        panelId={props.panelId}
        visualRecipients={props.visualRecipients}
        author={props.author}
        // We show the "new" pill separately
        isNewUnread={false}
        isClickable
        isUserAuthor={props.isUserAuthor}
        hasSmallFont
      />
    );
  }

  function maybeRenderPills() {
    // Never show a "NEW" unread indicator for auto-response messages.
    const isAutoResponse = computeIsAutoResponseMessageActivitySource(props.activitySourceVisualType);

    if (!props.needsSeenPill && !needsDetails && !props.isNewUnread) {
      return null;
    }

    if (props.message.type !== MessageTypesEnum.FRONT_CHAT) {
      return (
        <StyledSeenDiv>
          <MessageViewerUnread
            isNewUnread={props.isNewUnread}
            isUserAuthor={props.isUserAuthor}
            isAutoResponse={isAutoResponse}
          />
          {props.needsSeenPill && <MessageViewerSeen hasSmallPill message={props.message} />}
        </StyledSeenDiv>
      );
    }

    const recipient = first(props.visualRecipients.to);
    const displayName = recipient ? buildContactDisplayName(recipient) : undefined;
    if (!props.message.isInbound) {
      return (
        <StyledSeenDiv>
          <MessageViewerUnread
            isNewUnread={props.isNewUnread}
            isUserAuthor={props.isUserAuthor}
            isAutoResponse={isAutoResponse}
          />
          {props.message.isSeen && (
            <MessageViewerChatSeen hasSmallPill chatUserName={displayName} message={props.message} />
          )}
        </StyledSeenDiv>
      );
    }

    return null;
  }

  const areRecipientsHidden = useMemo(() => {
    if (!props.isChatFollowUp) {
      return false;
    }

    if (hasAttachments && !messageHasBody) {
      return false;
    }

    return true;
  }, [hasAttachments, messageHasBody, props.isChatFollowUp]);

  return (
    <StyledWrapperDiv $isInbound={props.message.isInbound}>
      {!props.isChatFollowUp && (
        <StyledMessageAvatarDiv>
          <StyledMessageViewerAvatar
            panelId={props.panelId}
            message={props.message}
            author={props.author || first(props.visualRecipients.from)}
            isChatAvatar
            isChatUserOnline={props.isChatUserOnline}
          />
        </StyledMessageAvatarDiv>
      )}
      <StyledTimestampDiv>
        <MessageViewerDate
          message={props.message}
          isInDragRegion={props.isInDragRegion}
          timelineVariation={timelineVariation}
        />
      </StyledTimestampDiv>
      <StyledMessageWrapper>
        {banner}
        <StyledMessageContainerDiv
          className={messageViewerMessageContainerClassName}
          $isFollowup={props.isChatFollowUp}
          $isForm={hasDecisionTreesOrQualBotsResponses}
          $areRecipientsHidden={areRecipientsHidden}
          $hasOneAttahmentOnly={
            isLoadedMessage(props.message) && props.message.attachments.length === 1 && !messageHasBody
          }
        >
          {!areRecipientsHidden && (
            <StyledRecipientsDiv $needsDetails={needsDetails}>{renderRecipients()}</StyledRecipientsDiv>
          )}
          <StyledActionsDiv $needsDetails={needsDetails}>
            {maybeRenderPills()}
            {props.message.isInbound && layerExtra && (
              <MessageViewerVisitorUrlPopover url={layerExtra.layerPageUrl} hasSmallIcon />
            )}
            {isLoadedMessage(props.message) && (
              <MessageViewerMoreContainer
                hasSmallIcon
                activityId={props.activityId}
                cellsContext={props.cellsContext}
                message={props.message}
                namespace={props.namespace}
                isOnlyMessage={props.isOnlyMessage}
                isOnlyActivity={props.isOnlyActivity}
                isFirstMessage={props.isFirstMessage}
                onReplyClick={props.onReplyClick}
                onReplyAllClick={props.onReplyAllClick}
                onForwardClick={props.onForwardClick}
                onCloneClick={props.onCloneClick}
                onDestroyConversationClick={props.onDestroyConversationClick}
                onPopoutClick={props.onPopoutClick}
                panelId={props.panelId}
              />
            )}
          </StyledActionsDiv>
          <StyledMessageContentDiv
            $isFollowup={props.isChatFollowUp}
            $reducedBottomPadding={!props.isChatFollowUp || (hasAttachments && !messageHasBody)}
            $noTopPadding={!props.isChatFollowUp || !areRecipientsHidden}
          >
            {messageHasBody && isLoadedMessage(props.message) && (
              <StyledMessageViewerChatBody
                $isFollowup={props.isChatFollowUp}
                message={props.message}
                timelineVariation={timelineVariation}
                isInbound={props.message.isInbound}
                qualBotResponses={qualBotResponses}
                isUserVerified={isUserVerified}
                hasMessageAnnotations={props.hasMessageAnnotations}
                panelId={props.panelId}
                annotations={props.annotations}
                onCreateAnnotatedComment={props.onCreateAnnotatedComment}
              />
            )}
            {hasAttachments && !areRecipientsHidden && (
              <StyledMessageAttachmentsInContentDiv>
                {isLoadedMessage(props.message) && (
                  <MessageViewerAttachmentsContainer
                    message={props.message}
                    timelineVariation={timelineVariation}
                    shouldScroll={false}
                  />
                )}
              </StyledMessageAttachmentsInContentDiv>
            )}
          </StyledMessageContentDiv>
          {hasAttachments && areRecipientsHidden && (
            <StyledMessageAttachmentsStandaloneDiv>
              {isLoadedMessage(props.message) && (
                <MessageViewerAttachmentsContainer
                  message={props.message}
                  timelineVariation={timelineVariation}
                  shouldScroll={false}
                />
              )}
            </StyledMessageAttachmentsStandaloneDiv>
          )}
        </StyledMessageContainerDiv>
      </StyledMessageWrapper>
    </StyledWrapperDiv>
  );
};
